import { Card, Col, Empty, Row, Typography, theme as antdTheme } from 'antd';
import {
  BookOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EditOutlined,
  PercentageOutlined,
  RightOutlined,
  RiseOutlined,
  ScheduleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { PageHeader } from '@/shared/components/page-header';
import { useAbility } from '@/auth/ability-provider';
import type { Actions, Subjects } from '@/auth/ability';

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  /** Ability gate. Hidden when `ability.can(action, subject)` is false. */
  can?: { action: Actions; subject: Subjects };
}

// Tiles tagged with `can: { action:'manage', subject:'Academics' }` are
// configuration screens — only roles holding ACADEMICS:MANAGE_CONFIG (V12)
// see them. Plain Teacher gets just the day-to-day use tiles.
const tiles: Tile[] = [
  {
    to: '/academics/class-subjects',
    title: 'Class subjects',
    description: 'Assign subjects per class section. Configure theory / practical marks and pass criteria.',
    icon: <BookOutlined />,
    can: { action: 'manage', subject: 'Academics' },
  },
  {
    to: '/academics/timetable',
    title: 'Timetable',
    description: 'Weekly grid per class section — period × day. Teacher conflict detection.',
    icon: <ScheduleOutlined />,
  },
  {
    to: '/academics/exam-schedule',
    title: 'Exam schedule',
    description: 'Per section × exam type × subject. Date, total marks, passing marks.',
    icon: <CalendarOutlined />,
    can: { action: 'manage', subject: 'Academics' },
  },
  {
    to: '/academics/marks',
    title: 'Marks entry',
    description: 'Enter or edit marks for a scheduled exam — full class roster on one screen.',
    icon: <EditOutlined />,
  },
  {
    to: '/academics/results',
    title: 'Results & report cards',
    description: 'Calculate weighted aggregates, view results per subject, publish report cards.',
    icon: <TrophyOutlined />,
  },
  {
    to: '/academics/promotion',
    title: 'Year-end promotion',
    description: 'Bulk-progress students to next class with per-student exceptions (repeat, skip, graduate, left).',
    icon: <RiseOutlined />,
    can: { action: 'manage', subject: 'Academics' },
  },
  {
    to: '/academics/period-slots',
    title: 'Period slots',
    description: 'Time bands used by the timetable. Adjust per school day.',
    icon: <ClockCircleOutlined />,
    can: { action: 'manage', subject: 'Academics' },
  },
  {
    to: '/academics/grading-scales',
    title: 'Grading scales',
    description: 'Percentage → grade bands. Activate one scale at a time; used by result calculation.',
    icon: <PercentageOutlined />,
    can: { action: 'manage', subject: 'Academics' },
  },
  {
    to: '/academics/weightages',
    title: 'Exam weightages',
    description: 'Per-class weightage of each exam type. Must total 100% before results can be calculated.',
    icon: <PercentageOutlined />,
    can: { action: 'manage', subject: 'Academics' },
  },
];

export function AcademicsHubPage() {
  const { token } = antdTheme.useToken();
  const ability = useAbility();
  const visibleTiles = tiles.filter((t) => !t.can || ability.can(t.can.action, t.can.subject));

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageHeader
        eyebrow="Module"
        title="Academics"
        description="Subjects, timetable, exams, marks, and report cards."
      />

      <Row gutter={[16, 16]}>
        {visibleTiles.length === 0 ? (
          <Col xs={24}>
            <Empty description="You don't have any academics tasks available." />
          </Col>
        ) : (
          visibleTiles.map((t) => (
            <Col key={t.to} xs={24} md={12} xl={8}>
              <Link to={t.to} style={{ textDecoration: 'none' }}>
                <Card
                  hoverable
                  className="lift-card"
                  style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                  styles={{ body: { padding: 20 } }}
                >
                  <div style={{ display: 'flex', gap: 14 }}>
                    <span
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        display: 'grid',
                        placeItems: 'center',
                        background: token.colorFillTertiary,
                        color: token.colorPrimary,
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    >
                      {t.icon}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <Typography.Text strong style={{ fontSize: 15 }}>
                          {t.title}
                        </Typography.Text>
                        <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />
                      </div>
                      <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                        {t.description}
                      </Typography.Text>
                    </div>
                  </div>
                </Card>
              </Link>
            </Col>
          ))
        )}
      </Row>

      {ability.can('manage', 'Academics') && (
        <Card>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            The catalogs of <b>subjects</b> and <b>exam types</b> are managed under <Link to="/setup/lookups">Setup → Lookup catalogs</Link>.
          </Typography.Paragraph>
        </Card>
      )}
    </div>
  );
}
